import { useEffect, useMemo, useState } from 'react'
import { Box, LinearProgress, TextField, Typography } from '@mui/material'
import { ChevronLeft, Search, TriangleAlert } from 'lucide-react'
import type { Benchmark, Producer, ProducerTotals } from '../../api/types'
import { CountChip, Eyebrow } from '../../components/primitives'
import { fold, num, pct } from '../../lib/format'
import { tokens } from '../../theme'



/**
 * The submission's standing on yield, and its producers as filters.
 *
 * Mirrors the run summary panel on the results screens: the headline figure,
 * the accumulated totals, then a list of rows that double as the filter.
 */
export function ProducerPanel({
  season, loading, benchmark, totals, producers, group, selectedFo, selected,
  onSelect, onBack, shareThreshold,
}: {
  /** The harvest season shown — the one asked for, while it loads. */
  season: number | null
  /** The figures on screen are the previous ones; the new ones are on their way. */
  loading?: boolean
  benchmark: Benchmark | null
  totals: ProducerTotals | undefined
  producers: Producer[]
  group: 'fo' | 'farmer'
  selectedFo: string | null
  selected: string | null
  onSelect: (producer: string | null) => void
  onBack: () => void
  shareThreshold: number
}) {
  const mean = benchmark?.mean_yield ?? null
  const years = benchmark?.years ?? []
  const aggregate = totals?.kg_ha ?? null

  // Filtered here rather than on the server: the whole list is already loaded,
  // and a round trip per keystroke would lag behind the typing.
  const [query, setQuery] = useState('')
  useEffect(() => setQuery(''), [group, selectedFo])
  const shown = useMemo(() => {
    const needle = fold(query.trim())
    if (!needle) return producers
    return producers.filter((p) => fold(p.producer ?? '').includes(needle))
  }, [producers, query])

  return (
    <Box
      component="aside"
      sx={{
        width: 340, flex: 'none', backgroundColor: 'background.paper',
        borderRight: `1px solid ${tokens.color.ink200}`,
        display: 'flex', flexDirection: 'column', overflowY: 'auto',
      }}
    >
      {/* Holds its height either way, so nothing below jumps. */}
      <LinearProgress sx={{ height: 2, flex: 'none', visibility: loading ? 'visible' : 'hidden' }} />
      <Box sx={{
        p: 5, borderBottom: `1px solid ${tokens.color.ink200}`,
        '& .figures': { opacity: loading ? 0.35 : 1, transition: `opacity ${tokens.motion.dur.fast}ms` },
      }}>
        <Eyebrow sx={{ mb: 2 }}>
          {benchmark?.item ?? 'Crop'} · {benchmark?.country_code ?? '—'}{season ? ` · ${season} season` : ''}
          {loading ? ' · loading…' : ''}
        </Eyebrow>
        <Box className="figures">
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
          <Typography
            sx={{
              font: `700 40px/1 ${tokens.font.display}`, letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums', color: tokens.color.brandDeep,
            }}
            data-testid="aggregate-yield"
          >
            {aggregate === null ? '—' : num(aggregate, 0)}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>kg/ha declared</Typography>
        </Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1.5 }}>
          {mean === null
            ? 'No FAO benchmark for this crop and country.'
            : `FAO ${benchmark!.window_years}-year mean ${num(mean, 1)} kg/ha` +
              (years.length ? ` · ${years[0]}–${years[years.length - 1]}` : '')}
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mt: 3.5 }}>
          <Stat testId="total-fields" value={num(totals?.fields ?? 0)} label="fields" />
          <Stat
            testId="total-production"
            value={totals?.production_kg == null ? '—' : num(totals.production_kg / 1000, 0)}
            label="tonnes"
          />
          <Stat
            testId="total-over"
            value={num(totals?.over_threshold ?? 0)}
            label="over threshold"
            colour={totals?.over_threshold ? tokens.color.warning : undefined}
          />
        </Box>
        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 2 }}>
          {totals?.pct_of_national == null
            ? 'Share of national production unknown.'
            : `${pct(totals.pct_of_national, 3)} of national production`}
        </Typography>
        </Box>
      </Box>

      {group === 'farmer' && (
        <Box
          component="button"
          type="button"
          onClick={onBack}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, border: 0, width: '100%',
            px: 5, py: 3, cursor: 'pointer', textAlign: 'left',
            backgroundColor: 'transparent', color: 'text.secondary',
            font: `500 13px ${tokens.font.sans}`,
            borderBottom: `1px solid ${tokens.color.ink200}`,
            '&:hover': { color: tokens.color.brandTeal },
          }}
        >
          <ChevronLeft size={14} strokeWidth={1.5} />
          {selectedFo}
        </Box>
      )}

      <Box sx={{ px: 5, pt: 3.5, pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
          {group === 'fo' ? 'Producers' : 'Farmers'}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          {query ? `${shown.length} of ${producers.length}` : 'over threshold'}
        </Typography>
      </Box>

      {producers.length > 6 && (
        <Box sx={{ px: 5, pb: 2 }}>
          <TextField
            size="small"
            fullWidth
            value={query}
            placeholder={group === 'fo' ? 'Filter producers' : 'Filter farmers'}
            onChange={(e) => setQuery(e.target.value)}
            inputProps={{ 'aria-label': group === 'fo' ? 'Filter producers' : 'Filter farmers' }}
            InputProps={{
              startAdornment: (
                <Box sx={{ display: 'flex', pr: 1.5, color: 'text.disabled' }}>
                  <Search size={14} strokeWidth={1.5} />
                </Box>
              ),
              sx: { height: 32, '& input': { height: 30, fontSize: 13 } },
            }}
          />
        </Box>
      )}

      <Box sx={{
        px: 2, pb: 4, display: 'flex', flexDirection: 'column',
        opacity: loading ? 0.35 : 1, transition: `opacity ${tokens.motion.dur.fast}ms`,
      }}>
        {query && shown.length === 0 && (
          <Typography variant="body2" sx={{ px: 3, py: 2, color: 'text.disabled' }}>
            No {group === 'fo' ? 'producer' : 'farmer'} matches “{query}”.
          </Typography>
        )}
        {shown.map((p) => (
          <ProducerRow
            key={p.producer ?? '—'}
            producer={p}
            active={selected === p.producer}
            drillable={group === 'fo'}
            shareThreshold={shareThreshold}
            onClick={() => onSelect(selected === p.producer ? null : p.producer)}
          />
        ))}
      </Box>
    </Box>
  )
}

function Stat({
  value, label, colour, testId,
}: { value: string; label: string; colour?: string; testId?: string }) {
  return (
    <Box>
      <Typography
        data-testid={testId}
        sx={{ font: `500 16px ${tokens.font.sans}`, color: colour ?? 'text.primary', fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}</Typography>
    </Box>
  )
}

/**
 * One producer: its name, how far its aggregate yield sits from the benchmark,
 * and the count of its fields over the threshold.
 */
function ProducerRow({
  producer, active, drillable, shareThreshold, onClick,
}: {
  producer: Producer
  active: boolean
  drillable: boolean
  shareThreshold: number
  onClick: () => void
}) {
  const over = producer.over_threshold
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={drillable ? 'Show this producer’s farmers' : undefined}
      sx={{
        display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2, border: 0, width: '100%',
        textAlign: 'left', cursor: 'pointer', borderRadius: `${tokens.radius.md}px`,
        backgroundColor: active ? tokens.color.ink100 : 'transparent',
        transition: `background ${tokens.motion.dur.fast}ms ${tokens.motion.easeStd}`,
        '&:hover': { backgroundColor: tokens.color.ink100 },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            font: `${active ? 500 : 400} 13px ${tokens.font.sans}`, color: 'text.primary',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}
        >
          {producer.producer ?? '—'}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
          {producer.kg_ha === null ? 'not reported' : `${num(producer.kg_ha, 0)} kg/ha`}
          {producer.pct_vs_benchmark !== null && ` · ${producer.pct_vs_benchmark > 0 ? '+' : ''}${num(producer.pct_vs_benchmark, 0)}%`}
        </Typography>
      </Box>

      {producer.share_flagged && (
        <Box
          title={`Claims ${pct(producer.pct_of_national, 4)} of national production, over the ${pct(shareThreshold, 3)} threshold`}
          sx={{ display: 'flex', color: tokens.color.danger, flex: 'none' }}
        >
          <TriangleAlert size={14} strokeWidth={2} />
        </Box>
      )}
      <CountChip value={over} />
    </Box>
  )
}
